import React, { useEffect, useState } from "react";
import StudioBrand from "./StudioBrand";
import { fetchCurrentUser, fetchStudioSummary, logout } from "../api/client";
import StudioCockpit from "./StudioCockpit";
import { resolveBranding } from "../presentationBranding";
import { flattenSpaces, getRootSpaces, getSpaceById } from "../spatialModel";
import DigitalTwinCanvas from "./DigitalTwinCanvas";
import SelectedSpace from "./SelectedSpace";
import SpaceNavigator from "./SpaceNavigator";
import { PUBLIC_ENTRY_PATH } from "../authRouting";
import { computeProfileCompletion } from "../profileCompletion";
import useStudioApplications from "../useStudioApplications";

const EMPTY_SUMMARY = {
  branding: { platform: { name: "HACaMS" }, deployment: {}, attribution: {} },
  studio: {},
  spatial: { spaces: {}, capabilities: {} },
};

export default function DigitalTwinStudio() {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [selectedSpaceId, setSelectedSpaceId] = useState("");
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const { applications, applicationsError, applicationsStale, applicationsLoading } = useStudioApplications(Boolean(user));
  const [summaryRefreshedAt, setSummaryRefreshedAt] = useState(null);
  const [summaryAttemptAt, setSummaryAttemptAt] = useState(null);

  useEffect(() => {
    let active = true;
    fetchCurrentUser().then((payload) => {
      if (active) setUser(payload.authenticated ? payload.user : null);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      const next = await fetchStudioSummary();
      if (!active) return;
      setSummaryAttemptAt(new Date());
      if (next) {
        setSummary(next);
        setSummaryRefreshedAt(new Date());
        setError("");
      } else {
        setError("The Studio spatial projection is temporarily unavailable.");
      }
    };

    refresh();
    const interval = setInterval(refresh, 5000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const spatial = summary.spatial || {};
  const spaces = getRootSpaces(spatial.spaces);
  const flatSpaces = flattenSpaces(spaces);
  const requestedSpace = getSpaceById(spaces, selectedSpaceId);
  const selectedSpace = requestedSpace || flatSpaces[0] || null;
  const branding = resolveBranding(summary.branding);
  const profileSummary = computeProfileCompletion(user);
  const profileDisplayName = user?.display_name || user?.username || "Studio member";
  const profileInitials = profileDisplayName.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").toUpperCase().slice(0, 2) || "H";

  const handleLogout = async () => {
    const result = await logout();
    if (!result.success) {
      setError("Could not sign out. Please try again.");
      return;
    }
    window.location.href = PUBLIC_ENTRY_PATH;
  };

  return (
    <main className="digital-twin-page">
      <div className="digital-twin-shell">
        <StudioBrand branding={branding} />

        {error && <div className="digital-twin-error" role="status">{error}</div>}

        <StudioCockpit
          user={user}
          profileDisplayName={profileDisplayName}
          profileInitials={profileInitials}
          profileCompletion={profileSummary.available ? `${profileSummary.percent}% · ${profileSummary.label}` : "Profile details unavailable"}
          onEditProfile={() => { window.location.href = "/profile"; }}
          onSignOut={handleLogout}
          hidePlatformKicker
          summary={summary}
          refreshError={error || applicationsError}
          applicationsStale={applicationsStale}
          applicationsLoading={applicationsLoading}
          stale={!summaryRefreshedAt || Date.now() - summaryRefreshedAt.getTime() > 15000}
          lastRefreshedAt={summaryRefreshedAt}
          lastAttemptAt={summaryAttemptAt}
          applications={applications}
          eventState={summary.class?.state || "UNKNOWN"}
          dominantState={summary.class?.state || "NO CLASS"}
          readiness={summary.readiness || {}}
          incidents={summary.incidents || []}
          cls={summary.class || {}}
          schedule={summary.schedule || []}
        />

        <div id="studio-spaces" className="digital-twin-workspace">
          <DigitalTwinCanvas
            spaces={spaces}
            capabilities={spatial.capabilities || {}}
            selectedSpaceId={selectedSpace?.id || ""}
            onSelect={setSelectedSpaceId}
          />
          <aside className="digital-twin-sidebar">
            <SpaceNavigator
              spaces={spaces}
              selectedSpaceId={selectedSpace?.id || ""}
              onSelect={setSelectedSpaceId}
              user={user}
              onLogout={handleLogout}
            />
            <SelectedSpace
              space={selectedSpace}
              capabilityCatalog={spatial.capabilities || {}}
            />
          </aside>
        </div>
      </div>
    </main>
  );
}